<script setup>
import { ref, inject } from "vue";
import AppLink from "@Components/AppLink.vue";
import { useRoute } from "vue-router";
import NProgress from "nprogress";
import { EllipsisVerticalIcon, PencilIcon, TrashIcon } from "@heroicons/vue/20/solid";
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { TrashIcon as TrashIconOutline, UserIcon } from "@heroicons/vue/24/outline";
import { useForm } from "@/form";
import { MagicIcon } from "@Icons/index";
import { useI18n } from "vue-i18n";
import Breadcrumb from "@Components/Breadcrumb.vue";
import { PrimaryButton } from "@Components/Button";
import { PlusIcon } from "lucide-vue-next";

const $notify = inject("$notify");
const { t: __ } = useI18n();
const route = useRoute();

const personas = ref();

const form = useForm({
    sort_order: route.query?.sort_order,
    sort_direction: route.query?.sort_direction,
});

const destroyModalOpen = ref(false);
const destroyForm = useForm({
    persona: undefined,
});

const processing = ref(false);

const createPersonaTool = {
    icon: UserIcon,
    name: __("Générer un profil IA"),
    description: __("Générez un profil IA pour WordPress et optimisez la rédaction de vos contenus."),
    href: { name: "personas.create" },
};

// methods
const fetch = async () => {
    NProgress.start();
    processing.value = true;
    personas.value = await form.post("personas_index");
    processing.value = false;
    NProgress.done();
};

const showDestroyModal = (persona) => {
    destroyForm.persona = persona.id;
    destroyModalOpen.value = true;
};

const destroy = () => {
    destroyForm
        .post("personas_destroy")
        .then(() => {
            destroyModalOpen.value = false;

            $notify({
                title: __("Le profil WordPress IA #{id} a été supprimé.", { id: destroyForm.persona }),
                type: "success",
            });

            destroyForm.reset();

            fetch();
        })
        .catch(() => {
            destroyModalOpen.value = false;

            $notify({
                title: __("Une erreur s'est produite lors de la suppression."),
                type: "error",
            });

            destroyForm.reset();
        });
};

await fetch();
</script>

<template>
    <div>
        <Breadcrumb :pages="[{ name: __('Mes rédacteurs IA') }]">
            <PrimaryButton :as="AppLink" :to="{ name: 'personas.create' }" :icon="PlusIcon">
                {{ __("Créer un rédacteur IA") }}
            </PrimaryButton>
        </Breadcrumb>
        <template v-if="personas.length > 0">
            <div class="grid grid-cols-1 2xl:grid-cols-3 lg:grid-cols-2">
                <div
                    v-for="(persona, index) in personas"
                    :class="['p-4 sm:p-6 lg:p-8 border-b border-gray-300 dark:border-gray-900',
                        index % 2 !== 1 ? 'lg:border-r' : '',
                        index % 3 !== 2 ? '2xl:border-r' : '',
                    ]">
                    <div class="flex gap-x-3">
                        <div class="flex flex-1 gap-x-3">
                            <img :src="persona.user_avatar?.url" class="w-24 h-24 object-cover bg-gray-50 rounded-lg dark:bg-gray-900" />
                            <div>
                                <div class="font-semibold text-gray-900 dark:text-white">{{ persona.user_first_name }} {{ persona.user_last_name }}</div>
                                <div class="mt-0.5 text-gray-600 dark:text-gray-400">{{ __("{age} ans", { age: persona.age }) }}</div>
                                <div class="mt-1 text-gray-600 dark:text-gray-400">{{ persona.job }}</div>
                            </div>
                        </div>
                        <div class="flex-none">
                            <Menu as="div" class="relative inline-block text-left">
                                <div>
                                    <MenuButton
                                        class="flex items-center text-gray-600 bg-white rounded-full dark:text-gray-400 dark:bg-gray-950 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-900">
                                        <span class="sr-only">Open options</span>
                                        <EllipsisVerticalIcon class="w-5 h-5" aria-hidden="true" />
                                    </MenuButton>
                                </div>

                                <transition
                                    enter-active-class="transition duration-100 ease-out"
                                    enter-from-class="transform opacity-0 scale-95"
                                    enter-to-class="transform opacity-100 scale-100"
                                    leave-active-class="transition duration-75 ease-in"
                                    leave-from-class="transform opacity-100 scale-100"
                                    leave-to-class="transform opacity-0 scale-95">
                                    <MenuItems
                                        class="w-56 max-h-60 overflow-auto absolute right-0 z-20 mx-6 mt-2 text-base bg-white rounded-md ring-1 ring-gray-300 shadow-lg origin-top-right dark:bg-gray-900 dark:ring-gray-800 focus:outline-none sm:text-sm">
                                        <div>
                                            <MenuItem v-slot="{ active, selected }">
                                                <AppLink
                                                    :to="{ name: 'personas.edit', params: { id: persona.id } }"
                                                    class="w-full relative block p-1 cursor-pointer select-none">
                                                    <div
                                                        :class="[
                                                            active
                                                                ? 'bg-gray-100 text-gray-900 dark:text-white dark:bg-gray-800'
                                                                : 'text-gray-700 dark:text-gray-300',
                                                            'rounded-sm py-2 px-3 flex items-center',
                                                        ]">
                                                        <PencilIcon
                                                            :class="[
                                                                active ? 'text-gray-500 dark:text-gray-300' : 'text-gray-400 dark:text-gray-400',
                                                                'w-5 h-5 mr-3',
                                                            ]"
                                                            aria-hidden="true" />
                                                        <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{
                                                            __("Modifier")
                                                        }}</span>
                                                    </div>
                                                </AppLink>
                                            </MenuItem>
                                            <MenuItem v-slot="{ active, selected }">
                                                <div @click="showDestroyModal(persona)" class="w-full relative block p-1 text-white cursor-pointer select-none">
                                                    <div
                                                        :class="[
                                                            active
                                                                ? 'bg-gray-100 text-gray-900 dark:text-white dark:bg-gray-800'
                                                                : 'text-gray-700 dark:text-gray-300',
                                                            'rounded-sm py-2 px-3 flex items-center',
                                                        ]">
                                                        <TrashIcon
                                                            :class="[
                                                                active ? 'text-gray-500 dark:text-gray-300' : 'text-gray-400 dark:text-gray-400',
                                                                'w-5 h-5 mr-3',
                                                            ]"
                                                            aria-hidden="true" />
                                                        <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{
                                                            __("Supprimer")
                                                        }}</span>
                                                    </div>
                                                </div>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </transition>
                            </Menu>
                        </div>
                    </div>
                    <div class="mt-3 text-xs line-clamp-5 text-gray-600 dark:text-gray-400">
                        {{ persona.user_description }}
                    </div>
                </div>
            </div>

            <TransitionRoot as="template" :show="destroyModalOpen">
                <Dialog as="div" class="z-[100000] relative" @close="destroyModalOpen = false">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0">
                        <div class="bg-opacity-75 fixed inset-0 bg-gray-900 transition-opacity" />
                    </TransitionChild>

                    <div class="z-[100000] overflow-y-auto fixed inset-0">
                        <div class="min-h-full flex justify-center items-end p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild
                                as="template"
                                enter="ease-out duration-300"
                                enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enter-to="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leave-from="opacity-100 translate-y-0 sm:scale-100"
                                leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel
                                    class="transform overflow-hidden relative px-4 pt-5 pb-4 text-left bg-gray-800 rounded-lg border border-gray-700 shadow-xl transition-all sm:w-full sm:max-w-lg sm:p-6 sm:my-8">
                                    <div class="sm:flex sm:items-start">
                                        <div
                                            class="flex-shrink-0 mx-auto w-12 h-12 flex justify-center items-center bg-red-500 rounded-full sm:w-10 sm:h-10 sm:mx-0">
                                            <TrashIconOutline class="w-6 h-6 text-gray-200" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-white">{{
                                                __("Supprimer le profil WordPress IA #{id}", { id: destroyForm.persona })
                                            }}</DialogTitle>
                                            <div class="mt-2">
                                                <p class="text-sm text-gray-400">
                                                    {{
                                                        __(
                                                            "Êtes-vous sûr de vouloir supprimer le profil WordPress IA #{id} ? Cette action ne peut pas être annulée.",
                                                            {
                                                                id: destroyForm.persona,
                                                            }
                                                        )
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:flex sm:flex-row-reverse sm:mt-4">
                                        <button
                                            type="button"
                                            class="w-full inline-flex justify-center items-center px-3 py-2 text-sm font-semibold text-white bg-red-500 rounded-md shadow-sm focus-visible:outline-2 focus-visible:outline-red-500 focus-visible:outline focus-visible:outline-offset-2 hover:bg-red-400 sm:w-auto sm:ml-3"
                                            @click="destroy">
                                            {{ __("Supprimer") }}
                                        </button>
                                        <button
                                            type="button"
                                            class="w-full inline-flex justify-center items-center px-3 py-2 mt-3 text-sm font-semibold text-gray-400 bg-gray-800 rounded-md focus-visible:outline-2 focus-visible:outline-gray-700 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-200 hover:bg-gray-700 hover:shadow-sm sm:w-auto sm:mt-0"
                                            @click="destroyModalOpen = false">
                                            {{ __("Annuler") }}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </template>
        <div v-else class="mx-auto max-w-xl py-16">
            <AppLink
                :to="{ name: 'personas.create' }"
                type="button"
                class="w-full relative block p-12 text-center rounded-lg border-2 border-gray-300 border-dashed group dark:border-gray-700 dark:hover:border-white focus:border-gray-900 focus:outline-none hover:border-gray-900">
                <UserIcon class="mx-auto w-12 h-12 text-gray-600 dark:text-gray-400" />
                <div class="mt-6 text-lg font-semibold text-gray-900 dark:text-white">{{ __("Aucun profil") }}</div>
                <div class="mt-3 text-sm text-gray-600 dark:text-gray-400">{{ __("Vous n'avez pas encore créé de profil.") }}</div>
                <div class="flex justify-center mt-8">
                    <PrimaryButton :icon="MagicIcon">
                        {{ __("Générer un profil IA") }}
                    </PrimaryButton>
                </div>
            </AppLink>
        </div>
    </div>
</template>
